import {
  EidProvider,
  Country,
  Language,
  PortalTab,
  ServiceType,
} from '@/types';
import { client } from '@/client';
import { CodeRequest } from '@/auth';

export type PortalLoginRequest =
  | { method: 'PASSWORD'; username: string; password: string }
  | { method: 'BANKID_SE' }
  | { method: 'FREJA' }
  | { method: 'BANKID_NO' }
  | { method: 'MITID' }
  | { method: 'FTN' };

export type PortalLoginStatusResponse = {
  id: string;
} & (
  | {
      status: 'IN_PROGRESS';
      url?: string;
      bankId?: {
        autoStartToken: string;
        qrData: string;
      };
      freja?: {
        qrData: string;
      };
    }
  | {
      status: 'COMPLETED';
      mustChangePassword: boolean;
      hasValidatedEmail: boolean;
      user: {
        id: number;
        username: string;
        displayName: string;
        country: Country | null;
        email: string | null;
      };
      companies: {
        companyCode: string;
        companyId: number;
        isAdmin: boolean;
        logAccess: boolean;
        companyName: string;
        services: ServiceType[] | null;
        countries: {
          teleidEnabled: boolean;
          teleidSsnEnabled: boolean;
          teleidEmailEnabled: boolean;
          teleidSmsEnabled: boolean;
          teleidChatEnabled: boolean;
          teleidMaxUsersNumber: number;
          teleidCounter: number;
          telesignEnabled: boolean;
          telesignSsnEnabled: boolean;
          telesignEmailEnabled: boolean;
          telesignSmsEnabled: boolean;
          telesignChatEnabled: boolean;
          telesignCounter: number;
          telesignMaxUsersNumber: number;
          safemailEnabled: boolean;
          safemailRepliesEnabled: boolean;
          safemailEmailEnabled: boolean;
          safemailSmsEnabled: boolean;
          safemailCounter: number;
          safemailMaxUsersNumber: number;
          provider: EidProvider;
          country: Country;
        }[];
      }[];
      sso?: {
        registrationId: string;
      };
    }
  | {
      status: 'FAILED';
      error: string;
    }
);

export type PortalAuthenticationRequest = {
  ref: string; // 'token' for changing company, 'refId' otherwise...
  companyId: number;
  force?: boolean;
};

export type PortalAuthenticationResponse = {
  token: string;
  user: {
    isAdmin: boolean;
    canSeeLogs: boolean;
    availableServices: {
      type: ServiceType;
      countries: Country[];
      providers: EidProvider[];
    }[];
  };
  company: {
    id: number;
    name: string;
    logoUrl: string;
    formIdOnlySafemail: boolean;
    defaultStartTab: PortalTab;
    telesignDefaultText: string | null;
    telesignDefaultTextDisabled: boolean;
    requestSignatureEnabled: boolean;
    safemailSignRequiresAttachment: boolean;
    safemailMultipleAttachments: boolean;
    defaultCountry: Country;
  };
};

export async function initPortalLogin(
  request: PortalLoginRequest,
  signal?: AbortSignal,
): Promise<PortalLoginStatusResponse> {
  return client
    .post('/portal/login', request, { signal })
    .then((response) => response.data);
}

export async function getPortalLoginStatus(
  id: string,
  signal?: AbortSignal,
): Promise<PortalLoginStatusResponse> {
  return client
    .get(`/portal/login/${encodeURIComponent(id)}`, {
      signal,
    })
    .then((response) => response.data);
}

export async function completePortalLogin(
  id: string,
  request: CodeRequest,
  signal?: AbortSignal,
): Promise<Extract<PortalLoginStatusResponse, { status: 'COMPLETED' }>> {
  return client
    .post(`/portal/login/${encodeURIComponent(id)}`, request, {
      signal,
    })
    .then((response) => response.data);
}

export async function ssoPortalLogin(
  code: string,
  signal?: AbortSignal,
): Promise<Extract<PortalLoginStatusResponse, { status: 'COMPLETED' }>> {
  return client
    .post('/auth/sso/login', { id: code, code }, { signal })
    .then((response) => response.data);
}

export async function completePortalAuthentication(
  request: PortalAuthenticationRequest,
  signal?: AbortSignal,
): Promise<PortalAuthenticationResponse> {
  return client
    .post('/portal/authenticate', request, { signal, withCredentials: true })
    .then((response) => response.data);
}

export async function changePortalCompany(
  request: PortalAuthenticationRequest,
  signal?: AbortSignal,
): Promise<PortalAuthenticationResponse> {
  return client
    .post('/portal/authenticate/company', request, {
      signal,
      withCredentials: true,
    })
    .then((response) => response.data);
}

export async function logoutPortal(signal?: AbortSignal): Promise<void> {
  return client
    .post('/portal/logout', null, { signal })
    .then((response) => response.data);
}

export async function resetPortalPassword(
  username: string,
  language: Language,
  signal?: AbortSignal,
): Promise<void> {
  return client
    .post(
      `/portal/reset-password`,
      {
        username,
        language,
      },
      { signal },
    )
    .then((response) => response.data);
}

export async function changePortalPassword(
  code: string,
  password: string,
  signal?: AbortSignal,
): Promise<void> {
  return client
    .post(
      '/portal/change-password',
      {
        code,
        password,
      },
      { signal },
    )
    .then((response) => response.data);
}
